/* GS2 — ground-up redesign behaviors.
   Vanilla JS. Cart drawer, predictive search, filters remain powered by the
   platform machinery; this file only drives the new gs2 surfaces. */
(function () {
  'use strict';

  /* ---------------- Mobile navigation ---------------- */
  function initMobileNav() {
    var burger = document.querySelector('[data-gs2-burger]');
    var panel = document.querySelector('[data-gs2-mobilenav]');
    if (!burger || !panel) return;
    var close = panel.querySelector('[data-gs2-mobilenav-close]');

    function setOpen(open) {
      panel.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }

    burger.addEventListener('click', function () {
      setOpen(!panel.classList.contains('is-open'));
    });
    if (close) close.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) setOpen(false);
    });
  }

  /* ---------------- Cart trigger: native-event toggle ----------------
     The platform cart drawer exposes 'cart:open' / 'cart:close' events and
     rebuilds its contents on 'ajaxProduct:added'. Driving it through those
     events (instead of synthetic clicks) is deterministic: no propagation
     races, no timing dependencies, and the state machine can't go stale. */
  function initCartTriggerGuard() {
    var manualMode = false;

    function isOpen(d) {
      return d.classList.contains('drawer--is-open');
    }

    function manualClose(d) {
      d.classList.remove('drawer--is-open');
      document.documentElement.classList.remove('js-drawer-open');
      manualMode = false;
    }

    function manualOpen(d) {
      /* Last-resort open: build contents through the platform's own
         'cart:build' hook, then show the drawer directly. Never touch
         inline display — the class-based CSS handles visibility, and an
         inline value would outlive the open state and break future opens. */
      document.dispatchEvent(new CustomEvent('cart:build'));
      d.style.removeProperty('display');
      void d.offsetHeight; /* reflow so the slide-in transition runs */
      d.classList.add('drawer--is-open');
      document.documentElement.classList.add('js-drawer-open');
      manualMode = true;
    }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;

      var d = document.getElementById('CartDrawer');

      /* When the drawer was opened manually, the platform's close handlers
         think it is closed — handle X-button and outside clicks ourselves. */
      if (manualMode && d && isOpen(d) && !e.target.closest('.js-drawer-open-cart')) {
        if (e.target.closest('.js-drawer-close') || !e.target.closest('.drawer')) {
          manualClose(d);
          return;
        }
      }

      var anchor = e.target.closest('.js-drawer-open-cart');
      if (!anchor) return;
      if (!d) return; /* no drawer on this page: let the link go to /cart */

      e.preventDefault();
      e.stopImmediatePropagation();

      if (isOpen(d)) {
        document.dispatchEvent(new CustomEvent('cart:close'));
        if (isOpen(d)) manualClose(d); /* native close didn't take */
        return;
      }

      /* Layer 1 — native. The leading cart:close resets any stale internal
         state (no-op when state is clean), then cart:open opens through the
         platform's own path: contents, focus trap, outside-click close. */
      document.dispatchEvent(new CustomEvent('cart:close'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      /* Layer 2 — the native path is synchronous, so if the class isn't on
         the drawer right now, that path is dead on this page: open manually. */
      if (!isOpen(d)) {
        manualOpen(d);
      }

      /* Layer 3 — only if BOTH layers failed to open it (drawer unusable on
         this page), go to the cart page rather than doing nothing. Never
         schedule this when the open succeeded, or a quick legitimate close
         would trigger a surprise navigation. */
      if (!isOpen(d)) {
        window.setTimeout(function () {
          var dd = document.getElementById('CartDrawer');
          if (dd && !isOpen(dd)) {
            window.location.href = anchor.getAttribute('href') || '/cart';
          }
        }, 400);
      }
    }, true);
  }

  /* Notify the platform drawer so it rebuilds its line items, then opens. */
  function announceCartAdd(item) {
    refreshCartCount();
    if (document.getElementById('CartDrawer')) {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item } }));
    } else {
      window.location.href = '/cart';
    }
  }

  /* ---------------- Discount code bar (cart drawer) ---------------- */
  function initDiscountBar() {
    var box = document.querySelector('[data-gs2-discount]');
    if (!box) return;
    var input = box.querySelector('[data-gs2-discount-input]');
    var btn = box.querySelector('[data-gs2-discount-apply]');
    var msg = box.querySelector('[data-gs2-discount-msg]');
    if (!input || !btn) return;

    function show(text, ok) {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = false;
      msg.classList.toggle('is-ok', !!ok);
      msg.classList.toggle('is-error', !ok);
    }

    function apply() {
      var code = (input.value || '').trim();
      if (!code) return;
      btn.disabled = true;
      var restore = btn.textContent;
      btn.textContent = '…';

      fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
        .then(function () {
          return fetch('/cart.js', { credentials: 'same-origin' });
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          /* Re-render the drawer so applied discounts and the discounted
             subtotal show. */
          document.dispatchEvent(new CustomEvent('cart:build'));

          var dc = (cart.discount_codes || []).filter(function (d) {
            return String(d.code).toLowerCase() === code.toLowerCase();
          })[0];

          if (dc && dc.applicable) {
            show('Code ' + code.toUpperCase() + ' applied.', true);
          } else if (dc && !dc.applicable) {
            show('That code does not apply to this cart.', false);
          } else if (cart.total_discount > 0 || (cart.cart_level_discount_applications || []).length) {
            show('Discount applied.', true);
          } else {
            show('Code saved. If valid, it will apply at checkout.', true);
          }
        })
        .catch(function () {
          show('Could not apply that code. Try again.', false);
        })
        .then(function () {
          btn.disabled = false;
          btn.textContent = restore;
        });
    }

    btn.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault(); /* the drawer is a checkout form — don't submit */
        apply();
      }
    });
  }

  /* ---------------- Cart count ---------------- */
  function refreshCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-gs2-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-visible', cart.item_count > 0);
        });
      })
      .catch(function () {});
  }

  /* ---------------- Product: gallery ---------------- */
  /* Scroll the thumb strip (horizontal only — never the page) so the
     active thumbnail sits fully in view, centered when possible. */
  function revealThumb(btn) {
    var strip = btn && btn.parentElement;
    if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
    var srect = strip.getBoundingClientRect();
    var brect = btn.getBoundingClientRect();
    var target = strip.scrollLeft + (brect.left - srect.left) - (srect.width - brect.width) / 2;
    if (strip.scrollTo) {
      strip.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      strip.scrollLeft = target;
    }
  }

  function initGallery(root) {
    var main = root.querySelector('[data-gs2-gallery-main]');
    if (!main) return;
    var vframes = Array.prototype.slice.call(root.querySelectorAll('[data-gs2-vframe]'));

    /* iOS: keep hosted videos inline instead of forcing fullscreen. */
    vframes.forEach(function (f) {
      var v = f.querySelector('video');
      if (v) {
        v.setAttribute('playsinline', '');
        v.playsInline = true;
      }
    });

    function deactivateFrame(f) {
      var v = f.querySelector('video');
      if (v) v.pause();
      var ifr = f.querySelector('iframe');
      if (ifr && ifr.getAttribute('src')) {
        /* Unloading the embed is the only reliable cross-host stop. */
        ifr.setAttribute('data-src', ifr.getAttribute('src'));
        ifr.removeAttribute('src');
      }
      f.classList.remove('is-active');
    }

    function hideVideo() {
      vframes.forEach(function (f) {
        if (f.classList.contains('is-active')) deactivateFrame(f);
      });
    }

    function showVideo(mediaId) {
      vframes.forEach(function (f) {
        if (f.getAttribute('data-media-id') === String(mediaId)) {
          var ifr = f.querySelector('iframe');
          if (ifr && !ifr.getAttribute('src') && ifr.getAttribute('data-src')) {
            ifr.setAttribute('src', ifr.getAttribute('data-src'));
          }
          f.classList.add('is-active');
        } else if (f.classList.contains('is-active')) {
          deactivateFrame(f);
        }
      });
    }

    /* Let the variant-change handler clear an open video before it swaps
       the main image. */
    root.gs2HideVideo = hideVideo;

    root.querySelectorAll('[data-gs2-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-media-type');
        if (type === 'video' || type === 'external_video') {
          showVideo(btn.getAttribute('data-media-id'));
        } else {
          hideVideo();
          var src = btn.getAttribute('data-src');
          var srcset = btn.getAttribute('data-srcset');
          if (!src) return;
          main.classList.add('is-fading');
          setTimeout(function () {
            main.src = src;
            if (srcset) main.srcset = srcset;
            main.classList.remove('is-fading');
          }, 160);
        }
        root.querySelectorAll('[data-gs2-thumb]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        revealThumb(btn);
      });
    });
  }

  /* ---------------- Product: variants ---------------- */
  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    value = parts.join('.');
    if (format && format.indexOf('{{') !== -1) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0])
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value);
    }
    return '$' + value;
  }

  function initProduct(root) {
    var dataEl = root.querySelector('[data-gs2-product-json]');
    if (!dataEl) return;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var select = root.querySelector('select[name="id"]');
    var atc = root.querySelector('[data-gs2-atc]');
    var priceEl = root.querySelector('[data-gs2-price]');
    var compareEl = root.querySelector('[data-gs2-compare]');
    var saveEl = root.querySelector('[data-gs2-save]');
    var stickyPrice = root.querySelector('[data-gs2-sticky-price]');
    var stickyAtc = root.querySelector('[data-gs2-sticky-atc]');
    var selLabels = root.querySelectorAll('[data-gs2-selected-option]');
    var factMetal = root.querySelector('[data-gs2-fact-metal]');
    var factSize = root.querySelector('[data-gs2-fact-size]');
    var factDim = root.querySelector('[data-gs2-fact-dim]');
    var factDimWrap = root.querySelector('[data-gs2-fact-dim-wrap]');
    var factEdition = root.querySelector('[data-gs2-fact-edition]');
    var factEditionWrap = root.querySelector('[data-gs2-fact-edition-wrap]');
    var factMintage = root.querySelector('[data-gs2-fact-mintage]');
    var factMintageWrap = root.querySelector('[data-gs2-fact-mintage-wrap]');
    var vmeta = {};
    var vmetaEl = root.querySelector('[data-gs2-vmeta]');
    if (vmetaEl) {
      try { vmeta = JSON.parse(vmetaEl.textContent); } catch (e) { vmeta = {}; }
    }

    /* Add-on tiles: checked state + metal-based visibility */
    var addons = Array.prototype.slice.call(root.querySelectorAll('[data-gs2-addon]'));
    addons.forEach(function (tile) {
      var input = tile.querySelector('.gs2-addon__input');
      if (!input) return;
      input.addEventListener('change', function () {
        tile.classList.toggle('is-checked', input.checked);
      });
    });

    function normKey(s) {
      return String(s || '').toLowerCase().replace(/\s+/g, '');
    }

    function updateAddonVisibility(opts) {
      addons.forEach(function (tile) {
        var visible = true;

        var only = tile.getAttribute('data-only-for');
        if (only && opts.indexOf(only) === -1) {
          visible = false;
        }

        /* Per-size pricing: when the add-on product has size variants, the
           tile only exists for sizes it actually offers. No matching
           variant, or an unavailable one, hides the tile. Single-variant
           add-ons (no map) always show at their flat price. */
        var mapEl = tile.querySelector('[data-gs2-addon-map]');
        if (visible && mapEl) {
          var map = null;
          try { map = JSON.parse(mapEl.textContent); } catch (e) { map = null; }
          if (map) {
            var match = null;
            for (var i = 0; i < opts.length; i++) {
              var key = normKey(opts[i]);
              if (key && map[key]) { match = map[key]; break; }
            }
            if (match && match.available) {
              tile.setAttribute('data-variant-id', match.id);
              var priceEl = tile.querySelector('[data-gs2-addon-price]');
              if (priceEl) priceEl.textContent = '+ ' + money(match.price, moneyFormat);
            } else {
              visible = false;
            }
          }
        }

        tile.style.display = visible ? '' : 'none';
        if (!visible) {
          var input = tile.querySelector('.gs2-addon__input');
          if (input && input.checked) {
            input.checked = false;
            tile.classList.remove('is-checked');
          }
        }
      });
    }

    /* Learn-more modal */
    var modal = root.querySelector('[data-gs2-addon-modal]');
    var modalTile = null;

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      modalTile = null;
      document.documentElement.style.overflow = '';
    }

    if (modal) {
      root.querySelectorAll('[data-gs2-addon-info]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var tile = btn.closest('[data-gs2-addon]');
          var tpl = tile && tile.querySelector('[data-gs2-addon-details]');
          if (!tpl) return;
          var content = modal.querySelector('[data-gs2-modal-content]');
          content.innerHTML = '';
          content.appendChild(tpl.content.cloneNode(true));
          var priceEl2 = modal.querySelector('[data-gs2-modal-price]');
          var tilePrice = tile.querySelector('[data-gs2-addon-price]');
          if (priceEl2) priceEl2.textContent = tilePrice ? tilePrice.textContent : '';
          modalTile = tile;
          modal.hidden = false;
          document.documentElement.style.overflow = 'hidden';
        });
      });

      modal.querySelectorAll('[data-gs2-modal-close]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });

      var modalAdd = modal.querySelector('[data-gs2-modal-add]');
      if (modalAdd) {
        modalAdd.addEventListener('click', function () {
          if (modalTile) {
            var input = modalTile.querySelector('.gs2-addon__input');
            if (input && !input.checked) {
              input.checked = true;
              modalTile.classList.add('is-checked');
            }
          }
          closeModal();
        });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
      });
    }

    function selectedAddonIds() {
      var ids = [];
      addons.forEach(function (tile) {
        if (tile.style.display === 'none') return;
        var input = tile.querySelector('.gs2-addon__input');
        if (input && input.checked) {
          var id = parseInt(tile.getAttribute('data-variant-id'), 10);
          if (id) ids.push(id);
        }
      });
      return ids;
    }

    function currentOptions() {
      var opts = [];
      root.querySelectorAll('[data-gs2-option-group]').forEach(function (group) {
        var checked = group.querySelector('input:checked');
        opts.push(checked ? checked.value : null);
      });
      return opts;
    }

    function findVariant(opts) {
      return product.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    function updateChipStates() {
      var opts = currentOptions();
      root.querySelectorAll('[data-gs2-option-group]').forEach(function (group, gi) {
        group.querySelectorAll('.gs2-chip').forEach(function (chip) {
          var input = chip.querySelector('input');
          var test = opts.slice();
          test[gi] = input.value;
          var v = findVariant(test);
          chip.classList.toggle('is-unavailable', !v || !v.available);
          chip.classList.toggle('is-selected', input.checked);
          var priceTag = chip.querySelector('[data-gs2-chip-price]');
          if (priceTag && v) priceTag.textContent = money(v.price, moneyFormat);
        });
      });
    }

    function render() {
      var opts = currentOptions();
      var variant = findVariant(opts);
      updateChipStates();

      selLabels.forEach(function (el) {
        var idx = parseInt(el.getAttribute('data-gs2-selected-option'), 10);
        if (!isNaN(idx) && opts[idx]) el.textContent = opts[idx];
      });

      if (factMetal && opts[0]) factMetal.textContent = opts[0] === 'Silver' ? '99.9% fine silver' : (opts[0] === 'Bronze' ? 'Solid bronze' : opts[0]);
      if (factSize && opts[1]) factSize.textContent = opts[1];

      if (!variant) {
        if (atc) { atc.disabled = true; atc.textContent = atc.getAttribute('data-label-unavailable') || 'Unavailable'; }
        return;
      }

      if (select) select.value = variant.id;

      /* Variant image: when the selected variant has a linked image, swap
         the gallery to it and highlight the matching thumbnail. */
      var mainImg = root.querySelector('[data-gs2-gallery-main]');
      var vimg = variant.featured_image && variant.featured_image.src ? String(variant.featured_image.src) : '';
      if (mainImg && vimg) {
        if (root.gs2HideVideo) root.gs2HideVideo();
        var vbase = vimg.split('?')[0];
        if ((mainImg.getAttribute('src') || '').split('?')[0] !== vbase) {
          mainImg.classList.add('is-fading');
          window.setTimeout(function () {
            mainImg.src = vbase + '?width=1100';
            mainImg.removeAttribute('srcset');
            mainImg.classList.remove('is-fading');
          }, 160);
        }
        root.querySelectorAll('[data-gs2-thumb]').forEach(function (b) {
          var bsrc = (b.getAttribute('data-src') || '').split('?')[0];
          b.classList.toggle('is-active', bsrc === vbase);
        });
        var activeThumb = root.querySelector('[data-gs2-thumb].is-active');
        if (activeThumb) revealThumb(activeThumb);
      }

      var meta = vmeta[String(variant.id)] || {};
      if (factDimWrap && factDim) {
        var dim = meta.dim || '';
        factDim.textContent = dim;
        factDimWrap.style.display = dim ? '' : 'none';
      }
      var edition = meta.edition || '';
      if (factEditionWrap && factEdition) {
        if (edition) factEdition.textContent = edition;
        factEditionWrap.style.display = edition ? '' : (factEdition.textContent ? '' : 'none');
      }
      if (factMintageWrap && factMintage) {
        var editionLower = (edition || (factEdition ? factEdition.textContent : '')).toLowerCase();
        var mintage = meta.mintage || '';
        var showMintage = mintage && (editionLower.indexOf('reserve') !== -1 || editionLower.indexOf('500') !== -1);
        factMintage.textContent = showMintage ? mintage : '';
        factMintageWrap.style.display = showMintage ? '' : 'none';
      }

      updateAddonVisibility(opts);

      if (priceEl) priceEl.textContent = money(variant.price, moneyFormat);
      if (stickyPrice) stickyPrice.textContent = money(variant.price, moneyFormat);

      /* Labor Day banner (present only inside the sale window): rate and
         you-pay price follow the selected variant's metal. */
      var saleBanner = root.querySelector('[data-gs2-sale]');
      if (saleBanner) {
        var saleMetal = String(variant.option1 || '').toLowerCase();
        var saleRate = saleMetal.indexOf('silver') !== -1 ? 10 : saleMetal.indexOf('bronze') !== -1 ? 20 : 0;
        if (saleRate > 0) {
          var rateEl = saleBanner.querySelector('[data-gs2-sale-rate]');
          var salePriceEl = saleBanner.querySelector('[data-gs2-sale-price]');
          if (rateEl) rateEl.textContent = saleRate + '% off';
          if (salePriceEl) salePriceEl.textContent = money(Math.round(variant.price * (100 - saleRate) / 100), moneyFormat);
          saleBanner.hidden = false;
        } else {
          saleBanner.hidden = true;
        }
      }

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareEl) {
        compareEl.textContent = onSale ? money(variant.compare_at_price, moneyFormat) : '';
        compareEl.style.display = onSale ? '' : 'none';
      }
      if (saveEl) {
        if (onSale) {
          saveEl.textContent = 'Save ' + money(variant.compare_at_price - variant.price, moneyFormat);
          saveEl.style.display = '';
        } else {
          saveEl.style.display = 'none';
        }
      }

      var soldOut = !variant.available;
      [atc, stickyAtc].forEach(function (b) {
        if (!b) return;
        b.disabled = soldOut;
        b.textContent = soldOut
          ? (b.getAttribute('data-label-soldout') || 'Sold out')
          : (b.getAttribute('data-label-add') || 'Add to cart');
      });

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    root.querySelectorAll('[data-gs2-option-group] input').forEach(function (input) {
      input.addEventListener('change', render);
    });

    /* AJAX add-to-cart -> open the platform cart drawer */
    var form = root.querySelector('form[data-gs2-product-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (atc && atc.disabled) return;
        var busyLabel = (atc && atc.getAttribute('data-label-adding')) || 'Adding…';
        var restore = atc ? atc.textContent : '';
        if (atc) { atc.disabled = true; atc.textContent = busyLabel; }

        var items = [{ id: parseInt(select.value, 10), quantity: 1 }];
        selectedAddonIds().forEach(function (id) {
          items.push({ id: id, quantity: 1 });
        });

        /* Optimistic open: slide the drawer out immediately and show the
           items being added; the real cart render replaces them shortly. */
        var drawerEl = document.getElementById('CartDrawer');
        if (drawerEl) {
          document.dispatchEvent(new CustomEvent('cart:open'));

          var productsEl = drawerEl.querySelector('[data-products]');
          if (productsEl) {
            drawerEl.querySelectorAll('.gs2-optimistic-item').forEach(function (n) { n.remove(); });
            drawerEl.classList.remove('is-empty');
            var wrapForm = drawerEl.querySelector('.drawer__contents');
            if (wrapForm) wrapForm.classList.remove('is-empty');

            function optimisticRow(imgSrc, title, metaText, priceText) {
              var row = document.createElement('div');
              row.className = 'gs2-optimistic-item';
              row.innerHTML =
                (imgSrc ? '<img src="' + imgSrc + '" alt="">' : '') +
                '<span class="gs2-optimistic-item__body">' +
                  '<span class="gs2-optimistic-item__title"></span>' +
                  '<span class="gs2-optimistic-item__meta"></span>' +
                '</span>' +
                '<span class="gs2-optimistic-item__price"></span>';
              row.querySelector('.gs2-optimistic-item__title').textContent = title;
              row.querySelector('.gs2-optimistic-item__meta').textContent = metaText;
              row.querySelector('.gs2-optimistic-item__price').textContent = priceText;
              return row;
            }

            var mainTitle = (root.querySelector('.gs2-buy__title') || {}).textContent || '';
            var mainMeta = currentOptions().filter(Boolean).join(' / ');
            var mainPrice = (priceEl || {}).textContent || '';
            var mainImg = root.querySelector('[data-gs2-gallery-main]');
            productsEl.prepend(optimisticRow(mainImg ? mainImg.src : '', mainTitle.trim(), mainMeta, mainPrice));

            addons.forEach(function (tile) {
              if (tile.style.display === 'none') return;
              var input = tile.querySelector('.gs2-addon__input');
              if (!input || !input.checked) return;
              var t = (tile.querySelector('.gs2-addon__title') || {}).textContent || '';
              var p = ((tile.querySelector('[data-gs2-addon-price]') || {}).textContent || '').replace('+ ', '');
              var img = tile.querySelector('.gs2-addon__media img');
              productsEl.prepend(optimisticRow(img ? img.src : '', t.trim(), 'Add-on', p));
            });
          }
        }

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
            return r.json();
          })
          .then(function (res) {
            announceCartAdd(res && res.items ? res.items[0] : res);
          })
          .catch(function (err) {
            document.querySelectorAll('.gs2-optimistic-item').forEach(function (n) { n.remove(); });
            document.dispatchEvent(new CustomEvent('cart:close'));
            alert(err.message || 'Could not add to cart.');
          })
          .then(function () {
            if (atc) { atc.disabled = false; atc.textContent = restore; }
            render();
          });
      });
    }

    if (stickyAtc) {
      stickyAtc.addEventListener('click', function () {
        if (form) form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      var buyCol = root.querySelector('.gs2-buy__actions');
      var bar = root.querySelector('[data-gs2-stickybar]');
      if (buyCol && bar && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var entry = entries[0];
          /* Off-screen alone isn't enough: at page load the buy block is
             below the fold. Show the bar only once it has been scrolled
             PAST (its box sits above the viewport top). */
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-active', passed);
        }, { rootMargin: '-90px 0px 0px 0px' }).observe(buyCol);
      }
    }

    render();
  }

  /* ---------------- Product cards: hover video ----------------
     Cards whose second media item is a hosted video carry a muted, looping
     <video> that the CSS fades in on hover; this drives play/pause.
     Delegated so cards injected later (recommendations, filtered grids)
     work without re-binding. */
  function initCardVideos() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function cardMediaFromEvent(e) {
      if (!e.target || !e.target.closest) return null;
      var media = e.target.closest('.gs2-card__media');
      if (!media || (e.relatedTarget && media.contains(e.relatedTarget))) return null;
      return media;
    }

    document.addEventListener('mouseover', function (e) {
      var media = cardMediaFromEvent(e);
      if (!media || reduceMotion.matches) return;
      var video = media.querySelector('[data-gs2-card-video]');
      if (video) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
    });

    document.addEventListener('mouseout', function (e) {
      var media = cardMediaFromEvent(e);
      if (!media) return;
      var video = media.querySelector('[data-gs2-card-video]');
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  /* ---------------- Boot ---------------- */
  function boot() {
    initMobileNav();
    initCartTriggerGuard();
    initDiscountBar();
    refreshCartCount();
    initCardVideos();
    document.querySelectorAll('[data-gs2-product]').forEach(function (root) {
      initGallery(root);
      initProduct(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
